import { type ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router'
import { cn } from '#app/utils/misc.tsx'
import { Icon } from './icon.tsx'
import { pressScale } from './motion.ts'

/** Marker for a collapsed gap of pages in {@link getPaginationRange}. */
export const PAGINATION_ELLIPSIS = 'ellipsis' as const

/** One slot in the rendered pager: a page number, or a collapsed gap. */
export type PaginationItem = number | typeof PAGINATION_ELLIPSIS

/**
 * Compute the visible page window: always the **first** and **last** page plus
 * the **current ± 1**, with the remaining runs collapsed to a single
 * {@link PAGINATION_ELLIPSIS}. A gap of exactly one page is never collapsed
 * (showing "1 … 3" when "1 2 3" fits would be silly), so an ellipsis only
 * appears where it hides two or more pages. `page` is clamped into
 * `[1, pageCount]` so an out-of-range query string still renders a sane window.
 */
export function getPaginationRange(
	page: number,
	pageCount: number,
): PaginationItem[] {
	if (pageCount <= 0) return []
	if (pageCount === 1) return [1]

	const current = Math.min(Math.max(1, Math.floor(page)), pageCount)
	const shown = [1, pageCount, current - 1, current, current + 1]
		.filter((n) => n >= 1 && n <= pageCount)
		.sort((a, b) => a - b)

	const items: PaginationItem[] = []
	let previous = 0
	for (const n of shown) {
		if (n === previous) continue // de-dupe overlapping window edges
		if (previous && n - previous > 1) items.push(PAGINATION_ELLIPSIS)
		items.push(n)
		previous = n
	}
	return items
}

/** Shared chip skeleton for every pager cell (numbers + prev/next controls). */
const pagerCellBase =
	'focus-cosy border-border inline-flex size-[var(--pager-size)] items-center justify-center rounded-md border text-body-xs'
/**
 * Interactive treatment shared by the navigable (non-active, non-disabled)
 * cells: hover fill plus the discrete press-scale (the pager is a discrete
 * pressable per §1.2).
 */
const pagerInteractive = `hover:bg-[var(--bg-hover)] ${pressScale}`

type PaginationProps = Omit<ComponentProps<'nav'>, 'children'> & {
	/** The current 1-based page. */
	page: number
	/** Total number of pages. */
	pageCount: number
	/**
	 * Map a page number to its href (e.g. `/blog?page=2`). Optional: when omitted,
	 * hrefs derive from the current URL — see {@link useDerivedPageHref}. Only pass
	 * this for a bespoke URL shape (path-based archives, in-page hash anchors).
	 */
	getPageHref?: (page: number) => string
}

/**
 * The default page-link builder: derive each page's href from the **current
 * URL**, so a paged list keeps whatever filters/search the URL already carries
 * without every route re-deriving that state. Clones the live search params,
 * sets `page`, and **omits** `page` at page 1 (keeping `/blog`'s canonical
 * bare-URL-for-page-1 shape; admin loaders default/clamp the param, so an
 * absent `page` reads as page 1 there too).
 */
function useDerivedPageHref(): (page: number) => string {
	const { pathname } = useLocation()
	const [searchParams] = useSearchParams()
	return (page: number) => {
		const params = new URLSearchParams(searchParams)
		if (page <= 1) params.delete('page')
		else params.set('page', String(page))
		const qs = params.toString()
		return qs ? `${pathname}?${qs}` : pathname
	}
}

/** A prev/next control: a real link when navigable, a disabled span at an end. */
function PagerControl({
	direction,
	href,
	disabled,
}: {
	direction: 'prev' | 'next'
	href: string | undefined
	disabled: boolean
}) {
	const { t } = useTranslation()
	const label =
		direction === 'prev' ? t('pagination.previous') : t('pagination.next')
	const icon = direction === 'prev' ? 'arrow-left' : 'arrow-right'
	const className = cn(
		pagerCellBase,
		disabled
			? 'text-muted-foreground pointer-events-none opacity-40'
			: pagerInteractive,
	)
	// A disabled control is a non-interactive span (links can't be `disabled`);
	// `aria-disabled` announces the state while it stays in the reading order. The
	// name comes from an sr-only text node, not `aria-label` — a naming attribute
	// on a roleless generic element trips axe `aria-prohibited-attr`.
	if (disabled) {
		return (
			<span aria-disabled="true" className={className}>
				<Icon name={icon} aria-hidden />
				<span className="sr-only">{label}</span>
			</span>
		)
	}
	return (
		<a aria-label={label} href={href} className={className}>
			<Icon name={icon} />
		</a>
	)
}

/**
 * Pagination — a numeric, link-based pager for server-paginated feeds (the
 * `/blog` index and tag archives). Real `<a>` links keep deep pages crawlable
 * and shareable; by default each href derives from the current URL
 * ({@link useDerivedPageHref}), so paging preserves the active filters/search
 * for free. Pass {@link PaginationProps.getPageHref} only for a bespoke URL shape.
 *
 * The visible window comes from {@link getPaginationRange} (first, last,
 * current ± 1, gaps as an ellipsis). The active page is marked
 * `aria-current="page"` on `--brand`; prev/next disable at the ends. The whole
 * control is wrapped in `<nav aria-label="Pagination">`. A lone page renders
 * nothing. Sizes from the `--pager-size` token; colors from
 * `--border`/`--accent`/`--brand` only.
 */
export function Pagination({
	page,
	pageCount,
	getPageHref,
	className,
	...props
}: PaginationProps) {
	const { t } = useTranslation()
	// Hook runs unconditionally (every real consumer renders inside the router);
	// the explicit prop, when given, wins over the URL-derived default.
	const derivedPageHref = useDerivedPageHref()
	const hrefFor = getPageHref ?? derivedPageHref
	if (pageCount <= 1) return null
	const items = getPaginationRange(page, pageCount)

	return (
		<nav
			aria-label={t('pagination.label')}
			className={cn('flex items-center justify-center gap-1.5', className)}
			{...props}
		>
			<PagerControl
				direction="prev"
				href={page > 1 ? hrefFor(page - 1) : undefined}
				disabled={page <= 1}
			/>
			{items.map((item, index) =>
				item === PAGINATION_ELLIPSIS ? (
					<span
						key={`ellipsis-${index}`}
						aria-hidden="true"
						className="text-muted-foreground text-body-xs inline-flex size-[var(--pager-size)] items-center justify-center"
					>
						…
					</span>
				) : (
					<a
						key={item}
						href={hrefFor(item)}
						aria-current={item === page ? 'page' : undefined}
						className={cn(
							pagerCellBase,
							'tabular-nums',
							item === page
								? 'bg-brand text-primary-foreground border-transparent font-semibold'
								: pagerInteractive,
						)}
					>
						{item}
					</a>
				),
			)}
			<PagerControl
				direction="next"
				href={page < pageCount ? hrefFor(page + 1) : undefined}
				disabled={page >= pageCount}
			/>
		</nav>
	)
}
